import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { EyeIcon, EyeSlashIcon, KeyIcon } from '@heroicons/react/24/outline';
import AuthFormLayout from './common/AuthFormLayout';

const ResetPasswordScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const getPasswordStrength = (password) => {
    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };
    Object.values(checks).forEach((check) => check && score++);
    return {
      score,
      checks,
      strength: score < 2 ? 'weak' : score < 4 ? 'medium' : 'strong',
    };
  };

  const passwordStrength = getPasswordStrength(formData.password);

  useEffect(() => {
    if (!token) {
      setError('No reset token provided');
      setIsVerifying(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setUserEmail(response.data.email);
      } catch (err) {
        if (err.response?.status === 400) {
          setError('This reset link is invalid or has expired. Please request a new one.');
        } else {
          setError('Unable to verify reset link. Please try again.');
        }
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.password) {
      setError('Please enter a new password');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (passwordStrength.score < 3) {
      setError('Please choose a stronger password');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await axios.post('/api/auth/reset-password', {
        token,
        new_password: formData.password,
      });
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/auth', {
          state: {
            message: 'Password reset successfully. Please sign in with your new password.',
          },
        });
      }, 3000);
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else if (err.request) {
        setError('Unable to connect to server. Please try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const strengthBarColor = (level) => {
    if (level <= passwordStrength.score) {
      if (passwordStrength.strength === 'weak') return 'bg-danger';
      if (passwordStrength.strength === 'medium') return 'bg-warning';
      return 'bg-pine';
    }
    return 'bg-hairline';
  };

  if (isVerifying) {
    return (
      <AuthFormLayout headline="Verifying reset link">
        <div className="flex flex-col items-center py-8 gap-4">
          <div className="w-10 h-10 border-2 border-hairline border-t-pine rounded-full animate-spin" />
          <p className="text-body text-secondary">Verifying reset link…</p>
        </div>
      </AuthFormLayout>
    );
  }

  if (isSuccess) {
    return (
      <AuthFormLayout
        headline="Password reset successful"
        subhead="Your password has been updated. Redirecting to sign in…"
      >
        <div className="flex justify-center py-4">
          <div className="w-14 h-14 rounded-full border border-hairline bg-pine-tint flex items-center justify-center">
            <KeyIcon className="w-7 h-7 text-pine" />
          </div>
        </div>
      </AuthFormLayout>
    );
  }

  if (!tokenValid) {
    return (
      <AuthFormLayout headline="Invalid reset link" subhead={error}>
        <Link to="/forgot-password" className="btn-primary w-full py-3 text-center block mb-3">
          Request new reset link
        </Link>
        <Link to="/auth" className="btn-ghost w-full text-center block">
          Back to sign in
        </Link>
      </AuthFormLayout>
    );
  }

  return (
    <AuthFormLayout
      headline="Reset password"
      subhead={`Create a new password for ${userEmail}`}
    >
      {error && (
        <div className="mb-4 p-4 rounded-md border border-danger bg-bone-raised text-body text-danger" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="password" className="block text-label text-secondary mb-2">
            New password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="input-editorial pr-10"
              placeholder="Enter new password"
              disabled={isLoading}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
            </button>
          </div>
          {formData.password && (
            <div className="mt-3">
              <div className="flex gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((level) => (
                  <div key={level} className={`h-1 flex-1 rounded-full ${strengthBarColor(level)}`} />
                ))}
              </div>
              <ul className="text-label text-secondary space-y-1">
                <li className={passwordStrength.checks.length ? 'text-pine' : ''}>
                  At least 8 characters
                </li>
                <li className={passwordStrength.checks.uppercase ? 'text-pine' : ''}>
                  Uppercase letter
                </li>
                <li className={passwordStrength.checks.number ? 'text-pine' : ''}>
                  Number
                </li>
                <li className={passwordStrength.checks.special ? 'text-pine' : ''}>
                  Special character
                </li>
              </ul>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="confirmPassword" className="block text-label text-secondary mb-2">
            Confirm new password
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className="input-editorial pr-10"
              placeholder="Confirm new password"
              disabled={isLoading}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? (
                <EyeSlashIcon className="w-5 h-5" />
              ) : (
                <EyeIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          {formData.confirmPassword && formData.password !== formData.confirmPassword && (
            <p className="text-label text-danger mt-1">Passwords do not match</p>
          )}
        </div>

        <button
          type="submit"
          disabled={
            isLoading ||
            !formData.password ||
            !formData.confirmPassword ||
            passwordStrength.score < 3
          }
          className="btn-primary w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <span className="w-5 h-5 border-2 border-bone border-t-transparent rounded-full animate-spin" />
              Updating password…
            </span>
          ) : (
            'Update password'
          )}
        </button>
      </form>

      <p className="text-center">
        <Link to="/auth" className="text-body text-pine hover:text-pine-deep">
          Back to sign in
        </Link>
      </p>
    </AuthFormLayout>
  );
};

export default ResetPasswordScreen;
